import type {
  AccountPayable,
  AccountPayableFilters,
  AccountPayableResponse,
  SingleAccountPayableResponse,
  PaymentFormData,
  PaymentResponse,
  AccountPayableFormData,
  CreateAccountPayableResponse,
  InvoiceFormData
} from '@/types/accounts-payable.types';
import { authFetch } from './api.service';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

class AccountsPayableService {
  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }
    return response.json();
  }

  async fetchAccounts(filters?: Partial<AccountPayableFilters>): Promise<AccountPayableResponse> {
    const queryParams = new URLSearchParams();

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
    }

    const response = await authFetch(`${API_BASE_URL}/accounts-payable?${queryParams}`);

    return this.handleResponse<AccountPayableResponse>(response);
  }

  async fetchAccount(accountId: string): Promise<SingleAccountPayableResponse> {
    const response = await authFetch(`${API_BASE_URL}/accounts-payable/${accountId}`);

    return this.handleResponse<SingleAccountPayableResponse>(response);
  }

  async createAccount(accountData: AccountPayableFormData): Promise<CreateAccountPayableResponse> {
    const response = await authFetch(`${API_BASE_URL}/accounts-payable`, {
      method: 'POST',
      body: JSON.stringify(accountData),
    });

    return this.handleResponse<CreateAccountPayableResponse>(response);
  }

  async registerPayment(paymentData: PaymentFormData): Promise<PaymentResponse> {
    const { accountPayableId, ...payload } = paymentData;

    const response = await authFetch(`${API_BASE_URL}/accounts-payable/${accountPayableId}/payments`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    return this.handleResponse<PaymentResponse>(response);
  }

  async getOverdueAccounts(): Promise<{ success: boolean; data: AccountPayable[] }> {
    const response = await authFetch(`${API_BASE_URL}/accounts-payable/reports/overdue`);

    return this.handleResponse<{ success: boolean; data: AccountPayable[] }>(response);
  }

  async getAccountsBySupplier(supplierId: string): Promise<{ success: boolean; data: AccountPayable[] }> {
    const response = await authFetch(`${API_BASE_URL}/accounts-payable/supplier/${supplierId}`);

    return this.handleResponse<{ success: boolean; data: AccountPayable[] }>(response);
  }

  async autoGenerateFromPurchaseOrder(purchaseOrderId: string): Promise<CreateAccountPayableResponse> {
    const response = await authFetch(`${API_BASE_URL}/accounts-payable/auto-generate`, {
      method: 'POST',
      body: JSON.stringify({ purchaseOrderId }),
    });

    return this.handleResponse<CreateAccountPayableResponse>(response);
  }

  async registerInvoice(accountId: string, invoiceData: InvoiceFormData): Promise<SingleAccountPayableResponse> {
    const payload = {
      invoiceNumber: invoiceData.invoiceNumber,
      invoiceDate: invoiceData.invoiceDate,
      invoiceAmount: invoiceData.amount,
      invoiceDueDate: invoiceData.dueDate,
      invoiceNotes: invoiceData.notes
    };

    const response = await authFetch(`${API_BASE_URL}/accounts-payable/${accountId}/invoice`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });

    return this.handleResponse<SingleAccountPayableResponse>(response);
  }

  // Método helper para formatear moneda
  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN',
      minimumFractionDigits: 2,
    }).format(amount);
  }

  // Método helper para formatear fechas
  formatDate(dateString: string): string {
    return new Intl.DateTimeFormat('es-PE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(new Date(dateString));
  }

  // Método helper para determinar si una cuenta está vencida
  isOverdue(account: AccountPayable): boolean {
    if (!account.dueDate || account.pendingAmount === 0) return false;
    return new Date(account.dueDate) < new Date();
  }

  // Método helper para obtener el color del estado
  getStatusColor(status: AccountPayable['status']): string {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'partially_paid':
        return 'bg-blue-100 text-blue-800';
      case 'paid':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  // Método helper para obtener el texto del estado
  getStatusText(status: AccountPayable['status']): string {
    switch (status) {
      case 'pending':
        return 'Pendiente';
      case 'partially_paid':
        return 'Pagada Parcialmente';
      case 'paid':
        return 'Pagada';
      default:
        return 'Desconocido';
    }
  }

  // Método helper para obtener el texto del método de pago
  getPaymentMethodText(method: string): string {
    const methods: Record<string, string> = {
      cash: 'Efectivo',
      check: 'Cheque',
      bank_transfer: 'Transferencia Bancaria',
      credit_card: 'Tarjeta de Crédito',
      debit_card: 'Tarjeta de Débito',
      other: 'Otro'
    };
    return methods[method] || method;
  }
}

export const accountsPayableService = new AccountsPayableService();
export default accountsPayableService;
